import React from 'react';
import { CheckIcon, AlertTriangleIcon } from '../icons';

const STAGE_COPY = {
  processing: 'Recording saved — will be sent to your email shortly.',
  uploading: 'Uploading your recording…',
  encoding: 'Adding the finishing touches…',
  sending_email: 'Sending your email…',
};

/**
 * A small stack of non-blocking status pills for recordings that are still
 * uploading/encoding/emailing in the background. Rendered on top of every
 * kiosk screen (idle, starting, recording) — it never gates starting a new
 * recording, it's purely informational. See useRecording.backgroundJobs.
 */
export default function BackgroundJobsBar({ jobs, onDismiss, onView }) {
  if (!jobs || jobs.length === 0) return null;

  return (
    <div style={s.wrap}>
      {jobs.map(job => {
        const isDone = job.status === 'completed';
        const isFailed = job.status === 'failed';
        const label = isDone
          ? 'Recording ready — email sent!'
          : isFailed
            ? (job.error_message || 'Something went wrong saving this recording.')
            : (STAGE_COPY[job.processing_stage] || STAGE_COPY.processing);

        return (
          <div key={job.id} style={{ ...s.pill, ...(isFailed ? s.pillFailed : isDone ? s.pillDone : {}) }} className="fade-in">
            <span style={s.iconWrap}>
              {isDone ? <CheckIcon size={16} color="var(--green)" />
                : isFailed ? <AlertTriangleIcon size={16} color="#d97706" />
                : <span style={s.spinner} />}
            </span>
            <div style={s.textCol}>
              <span style={s.label}>{label}</span>
              {job.player_email && <span style={s.sub}>{job.player_email}</span>}
            </div>
            {isDone && (
              <button style={s.linkBtn} onClick={() => onView(job)}>View</button>
            )}
            {(isDone || isFailed) && (
              <button style={s.closeBtn} onClick={() => onDismiss(job.id)} aria-label="Dismiss">✕</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

const s = {
  wrap: {
    position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
    zIndex: 300, display: 'flex', flexDirection: 'column', gap: 8,
    width: '100%', maxWidth: 420, padding: '0 16px',
  },
  pill: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: 'rgba(15,23,42,0.92)', color: 'white',
    borderRadius: 'var(--radius)', padding: '12px 14px',
    boxShadow: 'var(--shadow-lg)', backdropFilter: 'blur(12px)',
  },
  pillDone: { background: 'rgba(6,95,70,0.95)' },
  pillFailed: { background: 'rgba(146,64,14,0.95)' },
  iconWrap: { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20 },
  spinner: {
    width: 16, height: 16, borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.25)',
    borderTopColor: 'white',
    animation: 'spin 0.8s linear infinite',
    display: 'inline-block',
  },
  textCol: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  label: { fontSize: 13, fontWeight: 600, lineHeight: 1.4 },
  sub: {
    fontSize: 11, color: 'rgba(255,255,255,0.6)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  linkBtn: {
    flexShrink: 0, background: 'rgba(255,255,255,0.15)', border: 'none',
    color: 'white', fontSize: 12, fontWeight: 600,
    padding: '6px 10px', borderRadius: 20, cursor: 'pointer',
  },
  closeBtn: {
    flexShrink: 0, background: 'none', border: 'none',
    color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer',
    padding: 4,
  },
};
